"use client"

import { useState } from "react"
import { useFormContext } from "react-hook-form"
import { HomeWithHomeOwnerFormType } from "../schema/home-with-home-owner.schema"
import { computeGoodwillHomeStatus } from "../lib/goodwill-home-status"
import { Label } from "@/shared/components/ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Info } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

const GOODWILL_STATUS_HEADING = "Goodwill Opportunities"

const GOODWILL_STATUS_DESCRIPTIONS: Array<{ label?: string; text: string }> = [
  {
    text: "Optional volunteer projects outside the disaster relief and rebuilding recovery scope that may occur after a home is Functionally Complete or Complete.",
  },
  {
    text: "Examples: deck repairs, shed construction, exterior painting, accessibility improvements.",
  },
  {
    text: "This status is automatically derived from work scopes where the Goodwill toggle is enabled and reflects the overall progress of those goodwill projects.",
  },
  {
    text: 'If no goodwill work scopes are enabled, the status shows "No Assistance Required."',
  },
  { text: "Status options:" },
  { label: "Not Started", text: "Goodwill work identified but not yet started." },
  { label: "In Progress", text: "Goodwill work currently underway." },
  { label: "Complete", text: "All goodwill work finished." },
  { label: "No Assistance Required", text: "The home does not require goodwill assistance from any relief and/or rebuilding organization represented in ReliefHub or is outside the scope of those organizations. This may include situations where the homeowner completes the goodwill work themselves or the work is completed by another organization or contractor not represented in ReliefHub." },
  { label: "Unknown", text: "Goodwill status cannot yet be determined." },
]

interface GoodwillHomeStatusDisplayProps {
  className?: string
}

function GoodwillHomeStatusDisplay({ className }: GoodwillHomeStatusDisplayProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const formValues = useFormContext<HomeWithHomeOwnerFormType>().watch()
  const { session } = useSession()
  const isVolunteer = session?.role === UserRole.VOLUNTEER
  const { displayLabel, colorClass } = computeGoodwillHomeStatus(formValues)

  return (
    <div className={cn("space-y-2 w-full", className)}>
      <div className="flex items-center gap-1.5">
        <Label className="form-label transition-colors duration-200">Goodwill Status</Label>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button type="button" className="inline-flex text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded" aria-label="Goodwill Status explanation">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={8}
                className="w-[min(94vw,420px)] max-h-[40vh] md:max-h-[65vh] overflow-y-auto text-[11px] leading-tight"
              >
                <p className="font-semibold mb-1">{GOODWILL_STATUS_HEADING}</p>
                <div className="space-y-1">
                  {GOODWILL_STATUS_DESCRIPTIONS.map((item, idx) => (
                    <p key={`${item.label ?? "line"}-${idx}`}>
                      {item.label ? (
                        <>
                          <span className="font-semibold">{item.label}</span>
                          {" - "}
                          {item.text}
                        </>
                      ) : (
                        item.text
                      )}
                    </p>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <PopoverContent
            side="bottom"
            align="start"
            sideOffset={8}
            className="w-[min(94vw,440px)] max-h-[42vh] md:max-h-[72vh] overflow-y-auto text-xs leading-tight"
          >
            <p className="font-semibold mb-2">{GOODWILL_STATUS_HEADING}</p>
            <div className="space-y-2">
              {GOODWILL_STATUS_DESCRIPTIONS.map((item, idx) => (
                <p key={`${item.label ?? "line-pop"}-${idx}`}>
                  {item.label ? (
                    <>
                      <span className="font-semibold">{item.label}</span>
                      {" - "}
                      {item.text}
                    </>
                  ) : (
                    item.text
                  )}
                </p>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div
        className={cn(
          "flex h-9 w-full items-center rounded-md border px-3 py-2 text-sm shadow-sm transition-colors duration-200",
          colorClass
        )}
      >
        <span className={cn(isVolunteer && "opacity-50")}>{displayLabel}</span>
      </div>
    </div>
  )
}

export default GoodwillHomeStatusDisplay
